import { SiteId } from "@visioni/a3s-schema";
import type { ReportEmailDeliveryFrequency, ReportEmailDeliveryState } from "@visioni/a3s-reports/client-messages";

/**
 * The report email delivery page's pure decisions. Kept free of React and
 * `@src` imports so jest can test it.
 */

/**
 * The page's `siteId` route param as a `SiteId`, or `undefined` when it's
 * missing or malformed. Expo Router types a param as `string | string[]` (a
 * segment can repeat); a path segment never does, so the first value is used.
 */
export function parseEmailDeliverySiteId(param: string | string[] | undefined): SiteId | undefined {
  const value = Array.isArray(param) ? param[0] : param;
  if (value === undefined) return undefined;
  try {
    return SiteId.fromString(value);
  } catch {
    return undefined;
  }
}

/** The site's name from the tenant's site list, matched as `Breadcrumbs.web.tsx` does. */
export function emailDeliverySiteName(
  sites: readonly { readonly siteId: unknown; readonly siteName: string }[] | undefined,
  siteId: SiteId,
): string | undefined {
  return sites?.find((site) => String(site.siteId) === SiteId.toString(siteId))?.siteName;
}

/** What's saved for the site: its frequency when on, `undefined` when off (or not loaded). */
export function savedReportEmailDelivery(
  state: ReportEmailDeliveryState | undefined,
): ReportEmailDeliveryFrequency | undefined {
  if (state === undefined || !state.enabled) return undefined;
  return state.frequency === "daily" ? { frequency: "daily" } : { frequency: "weekly", weekday: state.weekday };
}

/** The HTTP status a failed request carried, from `ClientApiError` or the authenticated fetch's `ForbiddenError`. */
function errorStatus(error: unknown): number | undefined {
  if (typeof error !== "object" || error === null || !("status" in error)) return undefined;
  return typeof error.status === "number" ? error.status : undefined;
}

/** Why the delivery couldn't load: no access (`403`), or anything else, which is worth a retry. */
export function reportEmailDeliveryLoadFailure(error: unknown): "forbidden" | "failed" {
  return errorStatus(error) === 403 ? "forbidden" : "failed";
}

/** A refusal won't change on a retry; anything else (a 5xx, the network) might. */
export function shouldRetryReportEmailDeliveryLoad(failureCount: number, error: unknown): boolean {
  const status = errorStatus(error);
  return failureCount < 3 && (status === undefined || status >= 500);
}

export type ReportEmailDeliveryChange = "start" | "save" | "stop";

const SUCCESS_MESSAGES: Readonly<Record<ReportEmailDeliveryChange, string>> = {
  start: "Report emails started",
  save: "Changes saved",
  stop: "Report emails stopped",
};

const FAILURE_MESSAGES: Readonly<Record<ReportEmailDeliveryChange, string>> = {
  start: "Couldn't start your report emails. Please try again.",
  save: "Couldn't save your changes. Please try again.",
  stop: "Couldn't stop your report emails. Please try again.",
};

/** The backend refuses a new delivery above this many sites per user (`409 TooManyReportEmailDeliveries`). */
const MAX_DELIVERIES = 500;

export function reportEmailDeliverySuccessMessage(change: ReportEmailDeliveryChange): string {
  return SUCCESS_MESSAGES[change];
}

export function reportEmailDeliveryFailureMessage(change: ReportEmailDeliveryChange, error: unknown): string {
  if (errorStatus(error) === 409) {
    return `You can get report emails for up to ${String(MAX_DELIVERIES)} sites. Stop one to start another.`;
  }
  return FAILURE_MESSAGES[change];
}

/** A loaded list of the tenant's sites, as `useSites` returns it. */
type SiteList = readonly { readonly siteId: unknown; readonly siteName: string }[];

export interface ReportEmailDeliveryPageInputs {
  /** The decoded route param; `undefined` when missing or malformed. */
  readonly siteId: SiteId | undefined;
  readonly delivery: {
    readonly data: ReportEmailDeliveryState | undefined;
    readonly isError: boolean;
    readonly error: unknown;
  };
  readonly sites: {
    readonly data: SiteList | undefined;
    readonly isError: boolean;
  };
}

export type ReportEmailDeliveryPageStatus =
  | { readonly kind: "loading" }
  | { readonly kind: "notFound" }
  | { readonly kind: "forbidden" }
  | { readonly kind: "failed" }
  | { readonly kind: "ready"; readonly siteId: SiteId; readonly siteName: string };

/**
 * Which state the page is in. A failure only replaces the page while there is
 * nothing loaded to show: a background refetch that fails keeps the loaded
 * page, and with it any unsaved edits. A site-list failure is worth a retry,
 * not a "site not found".
 */
export function reportEmailDeliveryPageStatus({
  siteId,
  delivery,
  sites,
}: ReportEmailDeliveryPageInputs): ReportEmailDeliveryPageStatus {
  if (siteId === undefined) return { kind: "notFound" };
  if (delivery.data === undefined && delivery.isError) {
    return { kind: reportEmailDeliveryLoadFailure(delivery.error) };
  }
  if (sites.data === undefined && sites.isError) return { kind: "failed" };
  if (delivery.data === undefined || sites.data === undefined) return { kind: "loading" };

  const siteName = emailDeliverySiteName(sites.data, siteId);
  return siteName === undefined ? { kind: "notFound" } : { kind: "ready", siteId, siteName };
}
